/*** Featured Collection Carousel
  Active card is always centered on screen, responsive to all viewport sizes
 ***/

class FeaturedProductCarousel {
  static DESKTOP_BREAKPOINT = 990;
  static TABLET_BREAKPOINT = 750;
  static TRANSITION_DURATION = 500;
  static RESIZE_DEBOUNCE = 250;
  static DEFAULT_GAP = 40;

  constructor(element) {
    this.wrapper = element;
    this.track = this.wrapper.querySelector('[data-carousel-track]');
    this.prevButton = this.wrapper.querySelector('.carousel__button--prev');
    this.nextButton = this.wrapper.querySelector('.carousel__button--next');
    this.items = Array.from(this.track.querySelectorAll('.carousel__item'));
    this.navButtons = document.querySelectorAll('.product-carousel__nav-button');
    this.indicatorsContainer = this.wrapper.querySelector('.carousel__indicators');
    this.currentIndex = 0;
    this.resizeTimeout = null;
    this.autoplayInterval = null;
    this.isPaused = false;

    this.init();
  }

  init() {
    // Clone first and last items for infinite scroll effect
    if (this.items.length > 1) {
      const firstClone = this.items[0].cloneNode(true);
      const lastClone = this.items[this.items.length - 1].cloneNode(true);

      firstClone.classList.add('carousel__item--clone');
      lastClone.classList.add('carousel__item--clone');
      firstClone.setAttribute('inert', '');
      lastClone.setAttribute('inert', '');

      // Append first clone at end, prepend last clone at beginning
      this.track.appendChild(firstClone);
      this.track.insertBefore(lastClone, this.items[0]);

      // Update items array to include clones
      this.allItems = Array.from(this.track.querySelectorAll('.carousel__item'));

      // Start at index 0 for first real item (centered)
      this.currentIndex = 0;
      // Position 1 in the allItems array (first real card after the prepended clone)
      this.actualPosition = 1;
    } else {
      this.allItems = this.items;
      this.currentIndex = 0;
      this.actualPosition = 0;
    }

    this.setupAccessibility();
    this.createIndicators();
    this.attachEventListeners();

    // Set initial position without animation
    requestAnimationFrame(() => {
      this.setPosition(this.actualPosition, false);
      this.updateActiveStates();
    });
  }

  setupAccessibility() {
    // Add ARIA roles and labels
    this.wrapper.setAttribute('role', 'region');
    this.wrapper.setAttribute('aria-label', 'Featured products carousel');
    this.wrapper.setAttribute('aria-roledescription', 'carousel');

    this.track.setAttribute('role', 'list');

    this.items.forEach((item, index) => {
      item.setAttribute('role', 'listitem');
      item.setAttribute('aria-label', `Product ${index + 1} of ${this.items.length}`);
    });

    // Create live region for announcements
    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.className = 'visually-hidden';
    this.wrapper.appendChild(this.liveRegion);

    // Add ARIA labels to buttons
    this.prevButton?.setAttribute('aria-label', 'Previous product');
    this.nextButton?.setAttribute('aria-label', 'Next product');
  }

  setPosition(position, animate = true) {
    const item = this.allItems[position];
    if (!item) return;

    const itemWidth = item.offsetWidth;
    const gapValue = getComputedStyle(this.track).gap;
    const gap = gapValue ? parseFloat(gapValue) : FeaturedProductCarousel.DEFAULT_GAP;

    // Center the card: offset to card + half card width - half viewport width
    const scrollAmount = position * (itemWidth + gap) + itemWidth / 2 - window.innerWidth / 2;

    if (!animate) {
      this.track.style.transition = 'none';
    }

    this.track.style.transform = `translateX(-${scrollAmount}px)`;

    if (!animate) {
      this.track.offsetHeight; // Force reflow
      this.track.style.transition = '';
    }
  }

  createIndicators() {
    if (!this.indicatorsContainer) return;
    this.indicatorsContainer.innerHTML = '';

    this.items.forEach((_, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'carousel__indicator';
      button.setAttribute('aria-label', `Show product slide ${index + 1} of ${this.items.length}`);
      if (index === this.currentIndex) button.classList.add('is-active');

      button.addEventListener('click', () => this.goToSlide(index));
      this.indicatorsContainer.appendChild(button);
    });
  }

  goToSlide(index, animate = true) {
    this.currentIndex = index;
    this.actualPosition = index + 1; // +1 because of prepended clone

    this.setPosition(this.actualPosition, animate);
    this.updateActiveStates();
    this.announceSlide();
  }

  announceSlide() {
    if (this.liveRegion) {
      this.liveRegion.textContent = `Product ${this.currentIndex + 1} of ${this.items.length}`;
    }
  }

  updateActiveStates() {
    // Update nav buttons
    this.navButtons.forEach((btn, index) => {
      const isActive = index === this.currentIndex;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    // Update indicators
    const indicators = this.indicatorsContainer?.querySelectorAll('.carousel__indicator');
    indicators?.forEach((indicator, index) => {
      const isActive = index === this.currentIndex;
      indicator.classList.toggle('is-active', isActive);
      indicator.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    // Update carousel items (only original items, not clones)
    this.items.forEach((item, index) => {
      const isActive = index === this.currentIndex;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-current', isActive ? 'true' : 'false');
      if (isActive) {
        item.removeAttribute('inert');
      } else {
        item.setAttribute('inert', '');
      }
    });
  }

  next() {
    if (this.currentIndex >= this.items.length - 1) {
      this.actualPosition++;
      this.setPosition(this.actualPosition, true);

      setTimeout(() => {
        this.currentIndex = 0;
        this.actualPosition = 1;
        this.setPosition(this.actualPosition, false);
        this.updateActiveStates();
        this.announceSlide();
      }, FeaturedProductCarousel.TRANSITION_DURATION);
    } else {
      this.currentIndex++;
      this.goToSlide(this.currentIndex);
    }
  }

  prev() {
    if (this.currentIndex <= 0) {
      this.actualPosition--;
      this.setPosition(this.actualPosition, true);

      setTimeout(() => {
        this.currentIndex = this.items.length - 1;
        this.actualPosition = this.items.length;
        this.setPosition(this.actualPosition, false);
        this.updateActiveStates();
        this.announceSlide();
      }, FeaturedProductCarousel.TRANSITION_DURATION);
    } else {
      this.currentIndex--;
      this.goToSlide(this.currentIndex);
    }
  }

  attachEventListeners() {
    this.handlePrev = () => this.prev();
    this.handleNext = () => this.next();
    this.handleResize = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.goToSlide(this.currentIndex, false);
      }, FeaturedProductCarousel.RESIZE_DEBOUNCE);
    };
    this.handleKeydown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    };
    this.handleMouseEnter = () => this.pause();
    this.handleMouseLeave = () => this.resume();
    this.handleFocus = () => this.pause();
    this.handleBlur = () => this.resume();

    this.prevButton?.addEventListener('click', this.handlePrev);
    this.nextButton?.addEventListener('click', this.handleNext);

    this.navButtons.forEach((button, index) => {
      button.addEventListener('click', () => this.goToSlide(index));
    });

    // Keyboard navigation
    this.wrapper.setAttribute('tabindex', '0');
    this.wrapper.addEventListener('keydown', this.handleKeydown);

    // Pause on hover/focus
    this.wrapper.addEventListener('mouseenter', this.handleMouseEnter);
    this.wrapper.addEventListener('mouseleave', this.handleMouseLeave);
    this.wrapper.addEventListener('focusin', this.handleFocus);
    this.wrapper.addEventListener('focusout', this.handleBlur);

    window.addEventListener('resize', this.handleResize);

    // Add swipe support
    this.initSwipeSupport();
  }

  initSwipeSupport() {
    if (typeof SwipeHandler === 'undefined') return;

    this.swipeHandler = new SwipeHandler(this.track, {
      onSwipeLeft: () => this.next(),
      onSwipeRight: () => this.prev(),
      threshold: 50,
      restraint: 100,
      allowedTime: 500,
    });
  }

  pause() {
    this.isPaused = true;
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  resume() {
    this.isPaused = false;
  }

  destroy() {
    this.prevButton?.removeEventListener('click', this.handlePrev);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.wrapper.removeEventListener('keydown', this.handleKeydown);
    this.wrapper.removeEventListener('mouseenter', this.handleMouseEnter);
    this.wrapper.removeEventListener('mouseleave', this.handleMouseLeave);
    this.wrapper.removeEventListener('focusin', this.handleFocus);
    this.wrapper.removeEventListener('focusout', this.handleBlur);
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }
    if (this.swipeHandler) {
      this.swipeHandler.destroy();
    }
  }
}

// Initialize carousel when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const carouselWrapper = document.querySelector('.featured-product-carousel');
  if (carouselWrapper) {
    new FeaturedProductCarousel(carouselWrapper);
  }
});
